/**
 * useDeleteManagedSqlDb Hook
 *
 * Custom React Query mutation hook for deleting managed SQL databases.
 * Supports two-step 2FA verification flow.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { managedSqlDbService } from '../../../api/managed-sql-db.service';
import { getErrorMessage } from '../../../lib/utils/errors';
import type { TwoFactorRequiredResponse } from '../../../types/auth.types';

interface DeleteManagedSqlDbParams {
  managedSqlDbId: string;
  verificationCodeId?: string;
  verificationCode?: string;
}

interface UseDeleteManagedSqlDbOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  onTwoFactorRequired?: (response: TwoFactorRequiredResponse) => void;
}

/**
 * Hook for deleting managed SQL database with 2FA verification
 * @param tenantId - Tenant ID
 * @param options - Success/error/2FA callbacks
 */
export const useDeleteManagedSqlDb = (tenantId: string, options?: UseDeleteManagedSqlDbOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: DeleteManagedSqlDbParams) =>
      managedSqlDbService.deleteManagedSqlDb(
        tenantId,
        params.managedSqlDbId,
        params.verificationCodeId,
        params.verificationCode
      ),
    onSuccess: (data) => {
      // Check if 2FA is required
      if ('twoFactorRequired' in data && data.twoFactorRequired) {
        options?.onTwoFactorRequired?.(data);
        return;
      }

      // Actual deletion succeeded
      queryClient.invalidateQueries({ queryKey: ['managedSqlDbs', tenantId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
